import { FC, useMemo } from 'react';
import Typography from '@components/common/typography';
import styles from './styles.module.scss';
import InfoBlock from '@components/common/info_block';
import { IModalComponentProps } from '@context/modal_context';
import { EFundsOperation } from '@enum';
import { useUserContext } from '@hooks/context/useUserContext';
import { getFeesPlanSendAndRequest } from '@utils/payment';
import { getLoyaltyLevelString, prettifyAmount } from '@utils/index';
import { EProductName } from '@xeppt/xeppt-sdk/types';

const EstimatedFeeModal: FC<
    IModalComponentProps<{
        amount: number;
        method: EFundsOperation | 'card';
        fee: number;
        giftCardCount?: number;
    }>
> = ({ modalData }) => {
    const { loyalty, user, products } = useUserContext();

    const loyaltyPlan = useMemo(() => {
        if (modalData.method === 'card')
            return loyalty?.current_plan?.[EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT];
        return getFeesPlanSendAndRequest({ loyalty, method: modalData.method });
    }, [modalData, loyalty]);

    const renderDescription = useMemo(() => {
        //@ts-ignore
        const account = user?.business ? 'business' : 'personal';
        if (modalData?.method === EFundsOperation.E_TRANSFER_CREDIT_TRANSFER)
            return `Transfer from a ${account} account to <i>Interac</i> E-Transfer® account`;
        if (modalData?.method === 'card') return `Load prepaid XEPPT Card balance`;
        if (modalData?.method === EFundsOperation.E_TRANSFER_DEBIT_REQUEST)
            return `Request money from <i>Interac</i> E-Transfer® account to your ${account} account`;
        if (modalData?.method === EFundsOperation.PAY_BILL_CREDIT_PAYMENT)
            return `Transfer from a ${account} account to payees account`;
        if (modalData?.method === EFundsOperation.EPS_DEBIT_DEPOSIT)
            return `Load balance from linked card to your ${account} account`;
        if (modalData?.method === EFundsOperation.EFT_CREDIT_WITHDRAWAL)
            return `Transfer from a ${account} account to bank account`;
        if (modalData?.method === EFundsOperation.WALLET_CREDIT_TRANSFER)
            return `Transfer from a ${account} account to another XEPPT account`;
        if (modalData?.method === EFundsOperation.EFT_DEBIT_DEPOSIT)
            return `Load balance from bank to your ${account} account`;
        if (modalData?.method === EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM)
            return `Gift card redemption`;
    }, [modalData?.method]);

    const giftCardProduct = useMemo(() => {
        return products?.find(
            (item) => item.level === loyalty?.level && item.name === EProductName.OrderGiftCard
        );
    }, [products, loyalty]);

    const getInfoString = useMemo(() => {
        if (modalData?.method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD) {
            return `Gift card issuance incurs a $${prettifyAmount((giftCardProduct?.price || 0)?.toFixed(2))} CAD fee per gift card for users at the ${getLoyaltyLevelString(loyalty?.level)} level. This fee is separate from the gift card value.`;
        } else {
            return (
                <>
                    <span dangerouslySetInnerHTML={{ __html: renderDescription || '' }} /> incur a{' '}
                    {loyaltyPlan?.percent?.toFixed(2)}% + {loyaltyPlan?.flat?.toFixed(2)} CAD fee
                    for users at the {getLoyaltyLevelString(loyalty?.level)} level.
                </>
            );
        }
    }, [modalData, products, loyalty]);

    const getTitle = useMemo(() => {
        if (modalData?.method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD) {
            return 'Issuing fee';
        } else if (modalData?.method === EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM) {
            return 'Redemption fees';
        } else {
            return 'Estimated fees';
        }
    }, [modalData]);

    const getDescription = useMemo(() => {
        if (modalData?.method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD) {
            return 'Paid upon card creation';
        } else {
            return 'Included into amount you send';
        }
    }, [modalData]);

    const getResultAmount = useMemo(() => {
        if (modalData?.method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD) {
            return (modalData?.giftCardCount || 1) * (giftCardProduct?.price || 0);
        } else {
            return modalData?.fee;
        }
    }, [modalData, products]);

    const getTotalString = useMemo(() => {
        if (modalData?.method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD) {
            return 'Total';
        } else if (modalData?.method === EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM) {
            return (
                <>
                    Gift card redemption fee ({loyaltyPlan?.percent?.toFixed(2)}% +{' '}
                    {loyaltyPlan?.flat?.toFixed(2)} CAD)
                </>
            );
        } else {
            return (
                <>
                    XEPPT fee ({loyaltyPlan?.percent?.toFixed(2)}% + {loyaltyPlan?.flat?.toFixed(2)}{' '}
                    CAD)
                </>
            );
        }
    }, [modalData]);

    const isTotal = useMemo(
        () => modalData?.method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD,
        [modalData]
    );

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <Typography variant="h4" alignment="center">
                    {getTitle}
                </Typography>
            </div>
            <div className={styles.content}>
                {modalData?.method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD && (
                    <div className={styles.gift_card_wrapper}>
                        <div className={styles.row}>
                            <Typography variant="body4" className={styles.gray}>
                                Issuing fee per gift card
                            </Typography>
                            <Typography variant="body4" className={styles.gray}>
                                ${(giftCardProduct?.price || 0)?.toFixed(2)} CAD
                            </Typography>
                        </div>
                        {(modalData?.giftCardCount || 1) > 1 && (
                            <div className={styles.row}>
                                <Typography variant="body4" className={styles.gray}>
                                    Quantity of gift cards
                                </Typography>
                                <Typography variant="body4" className={styles.gray}>
                                    {modalData?.giftCardCount || 1}
                                </Typography>
                            </div>
                        )}
                    </div>
                )}
                <div className={styles.fees}>
                    <div className={styles.result}>
                        {isTotal ? (
                            <Typography variant="body3" weight="semibold">
                                {getTotalString}
                            </Typography>
                        ) : (
                            <Typography variant="body4" className={styles.gray}>
                                {getTotalString}
                            </Typography>
                        )}
                        <Typography variant="body3" weight="semibold">
                            ${getResultAmount?.toFixed(2)} CAD
                        </Typography>
                    </div>
                    <Typography variant="body4" className={styles.gray}>
                        {getDescription}
                    </Typography>
                </div>
                <InfoBlock variant="gray">{getInfoString}</InfoBlock>
            </div>
        </div>
    );
};

export default EstimatedFeeModal;
